import {
  PILL_CARET_GUARD,
  escapeAfterPill,
  findEnclosingPill,
} from "./pill-caret-guards";
import { VARIABLE_BLOCK_CLASS } from "./variable-block";
import { VARIABLE_PILL_CLASS } from "./variable-pill";

/**
 * Keyboard/selection guards around variable pills.
 *
 * Pills are `contenteditable="false"` atoms (variable-pill.ts), so the browser
 * itself keeps the caret out and deletes them whole; formatting reaches them
 * via Froala's `allowStylingOnNonEditable` (froala/config.ts). This module is
 * the belt to those
 * braces — it covers what the atom alone does not: Froala's hand-rolled
 * Backspace/Delete (which has its own ideas about non-editable inline nodes),
 * one-press arrow hops across the ZWS guard slots, and any stray selection
 * endpoint that still lands inside a pill (older engines, programmatic
 * restores). Three layers:
 *
 * 1. Selection normalisation (`computeSnap`). No selection endpoint may rest
 *    inside a pill: a stray caret is put back on the side it came from, a
 *    range endpoint is widened to swallow the whole pill. The pill is
 *    therefore either fully inside a selection (so formatting applies to all
 *    of it) or fully outside — never partially, so a token can no longer be
 *    split by styling half of it. Runs synchronously where it can — on
 *    `input` right after an edit mutates the DOM — so the caret never paints
 *    inside a pill; `selectionchange` (async) is the catch-all behind it.
 *
 * 2. Arrow hopping (`arrowJump`) — on `keydown`, a caret flush against a pill
 *    that steps toward it lands on the far side in one move, again before
 *    anything paints.
 *
 * 3. Edit interception (`decideBeforeInput`) — run on `beforeinput`, which
 *    fires before the DOM mutates and exposes the exact range the browser is
 *    about to touch. Any edit whose target range starts or ends inside a
 *    pill is cancelled; Backspace/Delete with the caret flush against a pill
 *    removes the pill whole, the way a single character would go.
 *
 *    Froala caveat: Froala 5 implements Backspace and Delete ITSELF on
 *    keydown (`cursor.backspace()` / `cursor.del()` + preventDefault), so no
 *    native `beforeinput`/`input` ever fires for those keys. They are
 *    therefore also handled in a document-level CAPTURE keydown listener
 *    (`deleteAtomAtCaret`), which runs before Froala's at-target listener in
 *    every browser and stops the event when it has removed a pill — or any
 *    other atom (`ATOM_SELECTOR`), which Froala's own deletion skips. A
 *    bubble listener on the editable then re-snaps synchronously after
 *    Froala's own manual deletion, standing in for the `input` event that
 *    never comes.
 *
 * 4. Hollow-pill sweep (`removeHollowPills`) — a pill span the browser or
 *    Froala emptied instead of removing still serialises back to
 *    `{{name}}`, resurrecting the variable. The sweep runs from every
 *    `input`, so a cut, a drag-out and an IME replacement are covered as
 *    well as Backspace/Delete. ⌘X additionally gets the keydown/`cut`
 *    pre-pass Backspace/Delete get, snapping a partial selection out of any
 *    pill before the removal runs.
 *
 * All layers are inert for content that contains no pill.
 */

export interface DomPoint {
  node: Node;
  offset: number;
}

/** A pill enclosing (or equal to) the position's container, else null. */
export const pillAtPoint = (node: Node | null): HTMLElement | null =>
  node ? findEnclosingPill(node) : null;

const indexOf = (node: Node): number =>
  Array.prototype.indexOf.call(node.parentNode?.childNodes ?? [], node);

/** Caret spot just BEFORE `pill`: end of a preceding text sibling, or a fresh
 * ZWS guard when there is none (mirror of `escapeAfterPill`). */
export const escapeBeforePill = (pill: Element): DomPoint => {
  const doc = pill.ownerDocument;
  let before = pill.previousSibling;
  if (!before || before.nodeType !== Node.TEXT_NODE) {
    before = doc.createTextNode(PILL_CARET_GUARD);
    pill.parentNode?.insertBefore(before, pill);
  }
  const text = before as Text;
  return { node: text, offset: text.length };
};

/** Boundary position in the pill's parent: `after` → right after the pill. */
const boundaryPoint = (pill: Element, after: boolean): DomPoint => ({
  node: pill.parentNode as Node,
  offset: indexOf(pill) + (after ? 1 : 0),
});

const comparePoints = (a: DomPoint, b: DomPoint): number => {
  const doc = a.node.ownerDocument ?? document;
  const r = doc.createRange();
  r.setStart(a.node, a.offset);
  r.collapse(true);
  return r.comparePoint(b.node, b.offset); // -1: b before a, 1: b after a
};

/** True when a caret at `point` sits in the first half of the pill's text. */
const inFirstHalf = (pill: Element, point: DomPoint): boolean => {
  const doc = pill.ownerDocument;
  const r = doc.createRange();
  r.setStart(pill, 0);
  try {
    r.setEnd(point.node, point.offset);
  } catch {
    return true;
  }
  const len = pill.textContent?.length ?? 0;
  return r.toString().length * 2 < len;
};

/**
 * Decide which side of `pill` a stray caret belongs on: the side it was on
 * before it strayed. `previous` is the caret position just before the change
 * (the last one seen outside any pill, or the pre-edit caret from
 * `beforeinput`). A caret only ends up inside a pill by accident — a boundary
 * collapsing after a deletion, a programmatic restore — so "put it back where
 * it was" is always right. Deliberate crossings (arrow keys) never reach this
 * function: `arrowJump` hops the pill at keydown instead. With no usable
 * `previous` (a click), fall back to the nearer half.
 */
export const sideForCaret = (
  pill: Element,
  caret: DomPoint,
  previous: DomPoint | null,
): "before" | "after" => {
  if (previous && previous.node.isConnected && !pillAtPoint(previous.node)) {
    try {
      const pillStart = boundaryPoint(pill, false);
      // The edit may have shortened previous.node — clamp so comparePoint
      // does not throw on a stale offset.
      const max =
        previous.node.nodeType === Node.TEXT_NODE
          ? (previous.node.textContent ?? "").length
          : previous.node.childNodes.length;
      const prev = {
        node: previous.node,
        offset: Math.min(previous.offset, max),
      };
      return comparePoints(pillStart, prev) < 0 ? "before" : "after";
    } catch {
      /* different tree — fall through */
    }
  }
  return inFirstHalf(pill, caret) ? "before" : "after";
};

export interface SnapResult {
  anchor: DomPoint;
  focus: DomPoint;
}

/**
 * Compute the corrected anchor/focus for a selection so that neither rests
 * inside a pill. Returns null when nothing needs to change. Pure w.r.t. the
 * selection object (reads only); may insert ZWS guard text nodes next to a
 * pill via escape*Pill when there is no text sibling to land in.
 */
export const computeSnap = (
  sel: Selection,
  previous: DomPoint | null,
): SnapResult | null => {
  if (sel.rangeCount === 0 || !sel.anchorNode || !sel.focusNode) return null;
  const anchor: DomPoint = { node: sel.anchorNode, offset: sel.anchorOffset };
  const focus: DomPoint = { node: sel.focusNode, offset: sel.focusOffset };
  const anchorPill = pillAtPoint(anchor.node);
  const focusPill = pillAtPoint(focus.node);
  if (!anchorPill && !focusPill) return null;

  if (sel.isCollapsed && anchorPill) {
    const side = sideForCaret(anchorPill, anchor, previous);
    const point =
      side === "after"
        ? escapeAfterPill(anchorPill)
        : escapeBeforePill(anchorPill);
    return { anchor: point, focus: point };
  }

  // Ranged selection: widen each stray endpoint to the pill's outer edge on
  // the side it is on (start → before, end → after), keeping direction.
  const range = sel.getRangeAt(0);
  const anchorIsStart =
    anchor.node === range.startContainer && anchor.offset === range.startOffset;
  const fix = (point: DomPoint, pill: HTMLElement | null, isStart: boolean) =>
    pill ? boundaryPoint(pill, !isStart) : point;
  return {
    anchor: fix(anchor, anchorPill, anchorIsStart),
    focus: fix(focus, focusPill, !anchorIsStart),
  };
};

const applySnap = (sel: Selection, snap: SnapResult): void => {
  sel.setBaseAndExtent(
    snap.anchor.node,
    snap.anchor.offset,
    snap.focus.node,
    snap.focus.offset,
  );
};

// ---------------------------------------------------------------------------
// beforeinput
// ---------------------------------------------------------------------------

export interface StaticRangeLike {
  startContainer: Node;
  startOffset: number;
  endContainer: Node;
  endOffset: number;
}

export type BeforeInputDecision =
  | { kind: "allow" }
  | { kind: "block" }
  | { kind: "delete-pill"; pill: HTMLElement };

// `split("")` rather than a spread: the tsconfig target predates iterable
// strings, and the guard is a single UTF-16 code unit anyway.
const isOnlyGuards = (text: string): boolean =>
  text.split("").every((ch) => ch === PILL_CARET_GUARD);

/**
 * The pill a collapsed caret is flush against on the given side, looking
 * through ZWS guard characters: `(parent, i)` boundaries and text positions
 * whose remaining characters toward the pill are all guards both count.
 */
export const adjacentPill = (
  caret: DomPoint,
  side: "before" | "after",
): HTMLElement | null => {
  const { node, offset } = caret;
  // `side === "before"`: the pill sits BEFORE the caret (Backspace target).
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? "";
    const towardPill =
      side === "before" ? text.slice(0, offset) : text.slice(offset);
    if (!isOnlyGuards(towardPill)) return null;
    const sibling = side === "before" ? node.previousSibling : node.nextSibling;
    return isPill(sibling) ? sibling : null;
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    const children = node.childNodes;
    const sibling = side === "before" ? children[offset - 1] : children[offset];
    return isPill(sibling) ? sibling : null;
  }
  return null;
};

const isPill = (node: Node | null | undefined): node is HTMLElement =>
  !!node &&
  node.nodeType === Node.ELEMENT_NODE &&
  (node as Element).classList.contains(VARIABLE_PILL_CLASS);

/**
 * Atoms Backspace/Delete removes whole, like a single character: pills and
 * the text editor's other contenteditable=false nodes. Froala's own
 * deletion leaves these in place when the caret is flush against one.
 */
const ATOM_SELECTOR = [
  `.${VARIABLE_PILL_CLASS}`,
  `.${VARIABLE_BLOCK_CLASS}`,
  "em-emoji",
  "i[data-is-remix-icon]",
  "i[data-is-font-awesome-icon]",
  ".fast-versioning",
  "[data-rkpi-active]",
].join(", ");

const isAtom = (node: Node | null | undefined): node is HTMLElement =>
  !!node &&
  node.nodeType === Node.ELEMENT_NODE &&
  (node as Element).matches(ATOM_SELECTOR);

/**
 * The atom Backspace (`before`) / Delete (`after`) at a collapsed caret must
 * remove whole: the node flush against the caret (ZWS guards are
 * see-through), or — with the caret at the very start/end of its inline
 * wrappers and paragraph — the node just past the outermost of them, below
 * `root`: `<atom><b>|ab</b>`, or a variable block before the paragraph the
 * caret starts. Never past a list item or table cell edge, where Froala's own
 * Backspace/Delete means unlist / leave the cell.
 */
const atomBesideCaret = (
  caret: DomPoint,
  side: "before" | "after",
  root: Node,
): HTMLElement | null => {
  const { node, offset } = caret;
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? "";
    const towardAtom =
      side === "before" ? text.slice(0, offset) : text.slice(offset);
    if (!isOnlyGuards(towardAtom)) return null;
  } else {
    const child = node.childNodes[side === "before" ? offset - 1 : offset];
    if (child) return isAtom(child) ? child : null;
  }
  for (let edge: Node | null = node; edge && edge !== root; ) {
    const sibling = side === "before" ? edge.previousSibling : edge.nextSibling;
    if (sibling) return isAtom(sibling) ? sibling : null;
    if (/^(LI|TD|TH)$/.test(edge.nodeName)) return null;
    edge = edge.parentNode;
  }
  return null;
};

const DELETE_BACKWARD = new Set([
  "deleteContentBackward",
  "deleteWordBackward",
  "deleteSoftLineBackward",
  "deleteHardLineBackward",
]);
const DELETE_FORWARD = new Set([
  "deleteContentForward",
  "deleteWordForward",
  "deleteSoftLineForward",
  "deleteHardLineForward",
]);
/**
 * Deletions the browser performs over a SELECTION rather than from a caret:
 * ⌘X and dragging the selection out. They have no caret-adjacent form, so
 * they must not take the whole-pill shortcut below (which reads the caret's
 * neighbours and would delete a pill the cut never covered). A range that
 * encloses a pill deletes it natively; an endpoint that strayed inside one is
 * refused, the selection snap having already widened such endpoints out of
 * the pill before the event fires. Whatever still manages to empty a pill
 * span is caught by the hollow-pill sweep in `attachAtomicPills`.
 */
const DELETE_BY_RANGE = new Set(["deleteByCut", "deleteByDrag"]);

/**
 * Classify an incoming edit. `caret` is the live selection's focus when the
 * selection is collapsed (null otherwise); `targetRanges` is
 * `InputEvent.getTargetRanges()`.
 */
export const decideBeforeInput = (
  inputType: string,
  targetRanges: readonly StaticRangeLike[],
  caret: DomPoint | null,
): BeforeInputDecision => {
  // 1. Whole-pill deletion when the caret is flush against a pill. Checked
  //    first: the browser's own target range for this case is INSIDE the
  //    pill (its last/first character), which rule 2 would merely block.
  if (caret && !DELETE_BY_RANGE.has(inputType)) {
    if (DELETE_BACKWARD.has(inputType)) {
      const pill = adjacentPill(caret, "before");
      if (pill) return { kind: "delete-pill", pill };
    } else if (DELETE_FORWARD.has(inputType)) {
      const pill = adjacentPill(caret, "after");
      if (pill) return { kind: "delete-pill", pill };
    }
  }

  // 2. Any edit that would touch a pill's interior is refused. A range that
  //    encloses the pill from the parent (`(parent,i)`→`(parent,i+1)`) has
  //    no endpoint inside it and stays allowed — that is "replace/delete the
  //    whole pill", the single-character semantics we want.
  for (const r of targetRanges) {
    if (pillAtPoint(r.startContainer) || pillAtPoint(r.endContainer)) {
      return { kind: "block" };
    }
  }
  return { kind: "allow" };
};

/**
 * A pill Froala's deletion emptied. `childNodes.length === 0` is the common
 * shape, but the deletion walks TEXT nodes only: a pill whose text was
 * wrapped in inline formatting (`<span pill><strong>{{x}}</strong></span>`)
 * keeps the now-empty `<strong>` and still looks populated. Anything left
 * that renders nothing — no text, or only ZWS caret guards — is hollow, and
 * serialising it back to `{{name}}` would resurrect the deleted variable.
 */
export const isHollowPill = (pill: Element): boolean =>
  pill.childNodes.length === 0 || isOnlyGuards(pill.textContent ?? "");

/**
 * Drop every hollowed-out pill under `root`, returning how many went. Safe to
 * run after ANY mutation — a cut, a drag-out, an IME replacement, Froala's own
 * range deletion — which is why `attachAtomicPills` calls it unconditionally
 * from `input` rather than only for the ranges its keydown path predicted.
 */
export const removeHollowPills = (root: Element): number => {
  let removed = 0;
  for (const pill of Array.from(
    root.querySelectorAll<HTMLElement>(`.${VARIABLE_PILL_CLASS}`),
  )) {
    if (isHollowPill(pill)) {
      pill.remove();
      removed += 1;
    }
  }
  return removed;
};

/**
 * Pills a ranged selection covers. After `computeSnap` no endpoint rests
 * inside a pill, so every pill the range intersects is covered whole — these
 * are the pills the coming deletion must take with it.
 */
export const pillsInRange = (root: Element, range: Range): HTMLElement[] =>
  Array.from(
    root.querySelectorAll<HTMLElement>(`.${VARIABLE_PILL_CLASS}`),
  ).filter((pill) => range.intersectsNode(pill));

/**
 * Finish a deletion that Froala performed over a range containing pills.
 * Froala's `selection.remove()` walks TEXT nodes, so it empties a
 * non-editable pill span and leaves the hollow `<span data-variable-name>`
 * behind — which serialises straight back to `{{name}}`, resurrecting the
 * variable the user just deleted. Remove the pills the range covered (and any
 * pill left without content), returning how many were removed.
 */
export const removeDeletedPills = (
  root: Element,
  doomed: readonly HTMLElement[],
): number => {
  let removed = 0;
  for (const pill of doomed) {
    if (pill.isConnected) {
      pill.remove();
      removed += 1;
    }
  }
  return removed + removeHollowPills(root);
};

/** Remove `pill` (or any atom) and leave the caret where it stood. */
const deletePill = (sel: Selection, pill: HTMLElement): void => {
  const parent = pill.parentNode;
  if (!parent) return;
  const at = boundaryPoint(pill, false);
  pill.remove();
  const doc = parent.ownerDocument ?? document;
  const r = doc.createRange();
  r.setStart(at.node, Math.min(at.offset, at.node.childNodes.length));
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
};

/**
 * Keydown twin of the `delete-pill` beforeinput decision, for editors (Froala)
 * that perform Backspace/Delete themselves and never let `beforeinput` fire.
 * With a collapsed caret next to an atom on the deletion side (see
 * `atomBesideCaret`), removes the atom whole and returns it; otherwise null
 * and the DOM is untouched.
 */
export const deleteAtomAtCaret = (
  sel: Selection,
  key: "Backspace" | "Delete",
  root: Node,
): HTMLElement | null => {
  if (sel.rangeCount === 0 || !sel.isCollapsed || !sel.focusNode) return null;
  const caret: DomPoint = { node: sel.focusNode, offset: sel.focusOffset };
  const side = key === "Backspace" ? "before" : "after";
  const atom = atomBesideCaret(caret, side, root);
  if (!atom) return null;
  deletePill(sel, atom);
  return atom;
};

// ---------------------------------------------------------------------------
// arrow keys
// ---------------------------------------------------------------------------

/**
 * Hop a pill in one keystroke. When the caret is flush against a pill (ZWS
 * guards are see-through) and the arrow points at it, move the focus to the
 * pill's far side instead of letting the browser step inside it. With
 * `extend` (Shift held) the anchor stays put, so the whole pill joins the
 * selection at once. Returns true when it handled the key — the caller must
 * then `preventDefault()`.
 */
export const arrowJump = (
  sel: Selection,
  key: "ArrowLeft" | "ArrowRight",
  extend: boolean,
): boolean => {
  if (sel.rangeCount === 0 || !sel.focusNode || !sel.anchorNode) return false;
  // A plain arrow on a range collapses it — native behaviour, leave it be.
  if (!extend && !sel.isCollapsed) return false;

  const focus: DomPoint = { node: sel.focusNode, offset: sel.focusOffset };
  const pillSide = key === "ArrowRight" ? "after" : "before";
  const pill = adjacentPill(focus, pillSide);
  if (!pill) return false;

  const target =
    pillSide === "after" ? escapeAfterPill(pill) : escapeBeforePill(pill);
  if (extend) {
    sel.setBaseAndExtent(
      sel.anchorNode,
      sel.anchorOffset,
      target.node,
      target.offset,
    );
  } else {
    sel.setBaseAndExtent(
      target.node,
      target.offset,
      target.node,
      target.offset,
    );
  }
  return true;
};

// ---------------------------------------------------------------------------
// wiring
// ---------------------------------------------------------------------------

/**
 * Make every `.sd-variable-pill` under `root` behave atomically. Returns a
 * disposer. Skips work while Froala has selection markers in the DOM (it is
 * mid save/restore and will place the selection itself).
 */
export const attachAtomicPills = (root: HTMLElement): (() => void) => {
  const doc = root.ownerDocument;
  // Last caret seen outside any pill — "the side it came from" for computeSnap.
  let lastOutside: DomPoint | null = null;
  // Caret captured in beforeinput, consumed by the matching input event.
  let preEditCaret: DomPoint | null = null;
  let snapping = false;

  const liveSelection = (): Selection | null => {
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.anchorNode) return null;
    if (!root.contains(sel.anchorNode)) return null;
    if (root.querySelector(".fr-marker")) return null;
    return sel;
  };

  /** Snap now if needed; true when the selection was moved. */
  const snapNow = (previous: DomPoint | null): boolean => {
    const sel = liveSelection();
    if (!sel) return false;
    const snap = computeSnap(sel, previous);
    if (!snap) return false;
    snapping = true;
    try {
      applySnap(sel, snap);
    } finally {
      snapping = false;
    }
    lastOutside = snap.focus;
    return true;
  };

  // Async catch-all: whatever the synchronous paths above missed.
  const onSelectionChange = () => {
    if (snapping) return;
    if (snapNow(lastOutside)) return;
    const sel = liveSelection();
    if (sel?.focusNode && !pillAtPoint(sel.focusNode)) {
      lastOutside = { node: sel.focusNode, offset: sel.focusOffset };
    }
  };

  // A click lands wherever the pointer says; the previous caret is no guide
  // to which side is meant, so let computeSnap use the nearer half.
  const onMouseDown = () => {
    lastOutside = null;
  };

  const notifyInput = (inputType: string) =>
    root.dispatchEvent(new InputEvent("input", { bubbles: true, inputType }));

  // Re-entrancy guard: the sweep announces itself with a synthetic `input`,
  // which lands back on `onInput`.
  let sweeping = false;
  const sweepHollowPills = (): number => {
    if (sweeping) return 0;
    sweeping = true;
    try {
      return removeHollowPills(root);
    } finally {
      sweeping = false;
    }
  };

  // Caret captured before Froala's manual Backspace/Delete, consumed by the
  // bubble-phase listener below (no native `input` fires for those keys).
  let preKeyCaret: DomPoint | null = null;
  // Pills covered by a ranged selection about to be deleted by Froala; its
  // deletion hollows them out instead of removing them (see
  // removeDeletedPills), so they are removed once it has run.
  let doomedPills: HTMLElement[] = [];
  // Same, for a cut. Kept SEPARATE from `doomedPills`: the keydown bubble
  // listener flushes that one immediately (Froala has already deleted by
  // then), whereas a cut's own removal happens AFTER keydown — flushing it
  // there would pull the pill out from under the cut.
  let doomedCutPills: HTMLElement[] = [];

  const flushDoomed = (doomed: readonly HTMLElement[]): void => {
    if (removeDeletedPills(root, doomed) > 0) {
      notifyInput("deleteContentBackward");
    }
  };

  const finishRangeDeletion = (): void => {
    if (doomedPills.length === 0) return;
    const doomed = doomedPills;
    doomedPills = [];
    flushDoomed(doomed);
  };

  const finishCut = (): void => {
    if (doomedCutPills.length === 0) return;
    const doomed = doomedCutPills;
    doomedCutPills = [];
    flushDoomed(doomed);
  };

  /**
   * ⌘X / context-menu Cut. A native cut removes an enclosed pill node whole
   * (nothing left to do), but an editor that implements cut itself walks TEXT
   * nodes the way Froala's Backspace does and leaves the pill span hollow.
   * Widen a partial selection out of any pill first, then clean up whatever
   * the removal left behind.
   */
  const prepareCut = (): void => {
    const sel = liveSelection();
    if (!sel || sel.isCollapsed) return;
    // A partial range still waiting on the async snap must be widened BEFORE
    // the removal sees it.
    snapNow(lastOutside);
    if (sel.rangeCount === 0 || sel.isCollapsed) return;
    doomedCutPills = pillsInRange(root, sel.getRangeAt(0));
    if (doomedCutPills.length > 0) requestAnimationFrame(finishCut);
  };

  // CAPTURE phase on the document: guaranteed to run before Froala's own
  // at-target keydown listener, whatever the browser's at-target ordering.
  const onKeyDownCapture = (e: KeyboardEvent) => {
    if (!(e.target instanceof Node) || !root.contains(e.target)) return;
    if (e.defaultPrevented) return;
    const sel = liveSelection();
    if (!sel) return;

    if (e.key === "Backspace" || e.key === "Delete") {
      // A partial range still waiting on the async snap must be widened
      // BEFORE Froala's `selection.remove()` sees it.
      snapNow(lastOutside);
      preKeyCaret =
        sel.isCollapsed && sel.focusNode
          ? { node: sel.focusNode, offset: sel.focusOffset }
          : null;
      doomedPills = sel.isCollapsed
        ? []
        : pillsInRange(root, sel.getRangeAt(0));
      if (doomedPills.length > 0) {
        // Froala deletes the range itself; the bubble listener and this rAF
        // then remove the pills it merely hollowed out.
        requestAnimationFrame(finishRangeDeletion);
        return;
      }
      if (deleteAtomAtCaret(sel, e.key, root)) {
        // Ours, not Froala's: stop its cursor.backspace()/del() outright.
        e.preventDefault();
        e.stopImmediatePropagation();
        preKeyCaret = null;
        notifyInput(
          e.key === "Backspace"
            ? "deleteContentBackward"
            : "deleteContentForward",
        );
        return;
      }
      // Froala will now delete a plain character. Its cleanup can strip the
      // ZWS guard that separated caret from pill and leave the caret in the
      // pill's tail. The bubble listener snaps it back synchronously; this
      // rAF (runs before paint, after every keydown listener regardless of
      // where Froala bound its own) is the belt to that braces.
      const caretBefore = preKeyCaret;
      if (caretBefore) {
        requestAnimationFrame(() => {
          snapNow(caretBefore);
        });
      }
      return;
    }

    // ⌘X / Ctrl+X. Handled at keydown like Backspace/Delete: whether the
    // removal comes from the browser or from Froala's own cut handling, the
    // selection must be pill-aligned before it runs.
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "x") {
      prepareCut();
      return;
    }

    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (arrowJump(sel, e.key, e.shiftKey)) {
      e.preventDefault();
      if (sel.focusNode) {
        lastOutside = { node: sel.focusNode, offset: sel.focusOffset };
      }
    }
  };

  // BUBBLE phase on the editable, registered after Froala's listener so it
  // runs once Froala's manual deletion has mutated the DOM (Froala calls
  // stopPropagation, which does not stop same-target listeners). Snap the
  // caret back to its pre-edit side before the frame paints.
  const onKeyDownBubble = () => {
    finishRangeDeletion();
    if (!preKeyCaret) return;
    snapNow(preKeyCaret);
    preKeyCaret = null;
  };

  const onBeforeInput = (e: Event) => {
    const ev = e as InputEvent;
    const sel = doc.getSelection();
    const caret: DomPoint | null =
      sel?.isCollapsed && sel.focusNode
        ? { node: sel.focusNode, offset: sel.focusOffset }
        : null;
    preEditCaret = caret;
    const ranges =
      typeof ev.getTargetRanges === "function" ? ev.getTargetRanges() : [];
    const decision = decideBeforeInput(ev.inputType, ranges, caret);
    if (decision.kind === "allow") return;
    if (!ev.cancelable) return; // e.g. insertCompositionText — nothing to do
    ev.preventDefault();
    if (decision.kind === "delete-pill" && sel) {
      deletePill(sel, decision.pill);
      // Froala tracks changes off key events, but let generic listeners know.
      root.dispatchEvent(
        new InputEvent("input", { bubbles: true, inputType: ev.inputType }),
      );
    }
  };

  // Synchronous, right after the DOM mutated and before paint: if the edit
  // collapsed a boundary into a pill (e.g. deleting the space after one),
  // put the caret back on the side it was on before the edit.
  const onInput = () => {
    snapNow(preEditCaret ?? lastOutside);
    preEditCaret = null;
    // Unconditional, not just from `finishRangeDeletion`: that only covers
    // the ranges the keydown path predicted, while ANY mutation can empty a
    // pill span — a cut, a drag-out, an IME replacement — and a hollow span
    // serialises straight back to `{{name}}`.
    if (sweepHollowPills() > 0) notifyInput("deleteContentBackward");
  };

  // Same pre-pass as the ⌘X keydown, for cuts that never produce one. Running
  // twice is harmless: `finishCut` consumes `doomedCutPills` once.
  const onCut = () => prepareCut();

  doc.addEventListener("selectionchange", onSelectionChange);
  doc.addEventListener("keydown", onKeyDownCapture, true);
  root.addEventListener("mousedown", onMouseDown);
  root.addEventListener("keydown", onKeyDownBubble);
  root.addEventListener("beforeinput", onBeforeInput);
  root.addEventListener("input", onInput);
  // The `cut` event covers the paths keydown does not see: the context menu,
  // the Edit menu, and any editor that triggers a cut programmatically.
  root.addEventListener("cut", onCut);
  return () => {
    doc.removeEventListener("selectionchange", onSelectionChange);
    doc.removeEventListener("keydown", onKeyDownCapture, true);
    root.removeEventListener("mousedown", onMouseDown);
    root.removeEventListener("keydown", onKeyDownBubble);
    root.removeEventListener("beforeinput", onBeforeInput);
    root.removeEventListener("input", onInput);
    root.removeEventListener("cut", onCut);
  };
};
